import { Button, Stack, Table } from 'react-bootstrap';
import { MdModeEdit, MdDelete, MdEdit, MdDeleteOutline } from 'react-icons/md';
import { useDispatch, useSelector } from 'react-redux';
import { showModal } from '../../../store/slices/modal/modalSlice';
import { EmptyTableMessage } from '../../../sections/components/EmptyTableMessage';
import { useTranslation } from 'react-i18next';
import { deleteCategoryById } from '../../../store/slices/category/categoryThunk';
import { toast } from 'react-toastify';
import { usePermission } from '../../../hooks/usePermission';
import { PERMISSIONS } from '../../../permissions/permissions';
import { useMemo } from 'react';

export function CategoryTable({ fetchCategory, editCategory, search }) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const category = useSelector((state) => state.category);
  const { currentLanguage } = useSelector((state) => state.language);
  const canEditCategory = usePermission(PERMISSIONS.CATEGORY.UPDATE);
  const canDeleteCategory = usePermission(PERMISSIONS.CATEGORY.DELETE);

  const filteredCategory = useMemo(() => {
    if (!search) return category.categories;
    return category.categories.filter((item) => {
      const translation = item.translationsMap?.[currentLanguage] || item.translationsMap?.['en'] || {};

      return translation.name.toLowerCase().includes(search.toLowerCase());
    });
  }, [category.categories, search]);
  const onDelete = (id) => {
    dispatch(
      showModal({
        title: t('menu_management.category.deleteCategory'),
        message: t('menu_management.category.sureWantToDelete'),
        type: 'confirm',
        onConfirm: () => deleteCategory(id)
      })
    );
  };

  const deleteCategory = (id) => {
    dispatch(deleteCategoryById(id))
      .unwrap()
      .then(() => {
        // Logic only for what happens AFTER success
        dispatch(fetchCategory());
      })
      .catch(() => {
        // You don't even need to do anything here anymore!
        // The middleware handles the toast and logs.
      });
  };

  const onEdit = (item) => {
    editCategory(item);
  };

  return (
    <>
      <Table responsive hover className="mb-0" style={{ tableLayout: 'fixed', width: '100%' }}>
        <thead>
          <tr>
            <th style={{ width: '80px' }}>{t('menu_management.category.sno')}</th>
            <th style={{ width: '25%' }}>{t('menu_management.category.categoryName')}</th>
            <th>{t('menu_management.category.categoryDescription')}</th>
            {(canDeleteCategory || canEditCategory) && <th style={{ width: '120px', textAlign: 'center' }}>{t('actions')}</th>}
          </tr>
        </thead>
        <tbody>
          {Array.isArray(filteredCategory) && filteredCategory.length > 0 ? (
            filteredCategory.map((item, index) => {
              const translation = item.translationsMap?.[currentLanguage] || item.translationsMap?.['en'] || {};

              return (
                <tr key={item.id} className="align-middle">
                  <td>{index + 1}</td>
                  <td className="text-truncate" title={translation.name}>
                    {translation.name || '-'}
                  </td>
                  <td
                    style={{
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis'
                    }}
                    title={translation.description}
                  >
                    {translation.description || '-'}
                  </td>
                  {(canDeleteCategory || canEditCategory) && (
                    <td>
                      <Stack direction="horizontal" gap={2} className="justify-content-center">
                        {canEditCategory && (
                          <Button
                            variant="light"
                            className="shadow-sm rounded-circle p-0 d-flex align-items-center justify-content-center"
                            onClick={() => onEdit(item)}
                            style={{ width: '35px', height: '35px' }}
                          >
                            <MdEdit size={18} style={{ color: 'var(--pc-sidebar-active-color)' }} />
                          </Button>
                        )}
                        {canDeleteCategory && (
                          <Button
                            variant="light"
                            className="shadow-sm rounded-circle p-0 d-flex align-items-center justify-content-center"
                            onClick={() => onDelete(item.id)}
                            style={{ width: '35px', height: '35px' }}
                          >
                            <MdDeleteOutline size={18} className="text-danger" />
                          </Button>
                        )}
                      </Stack>
                    </td>
                  )}
                </tr>
              );
            })
          ) : (
            <EmptyTableMessage colSpan={4} message="No Category Found" />
          )}
        </tbody>
      </Table>
    </>
  );
}
